// src/components/call/CallOverlay.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { acceptCall, hangup, rejectCall, toggleCam, toggleMic } from '../../actions/call.actions';
import { callService } from '../../calls';
import { unlockAudio } from '../../utils/audioUnlock';
import {
    canSetSink, canPickOutput, pickOutputDeviceId, listAudioOutputs,
    routeToDevice, routeFallbackIOS, isIOS, isMobile
} from '../../utils/audioRoute';

const fmt = (ms) => {
    if (!ms || ms < 0) return '00:00';
    const sec = Math.floor(ms / 1000);
    const m = String(Math.floor(sec / 60)).padStart(2, '0');
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
};

export default function CallOverlay({ getPeerName }) {
    const dispatch = useDispatch();
    const st = useSelector(s => s.call);
    const chats = useSelector(s => s.chat?.chats || []);
    const meId = useSelector(s => s.auth?.user?._id || s.auth?.user?.id || null);

    const [elapsed, setElapsed] = useState(0);
    const [min, setMin] = useState(false);
    const [facing, setFacing] = useState('user');
    const [quality, setQuality] = useState({ level: 0, rtt: 0, jitter: 0, lossPct: 0, bitrateIn: 0, bitrateOut: 0 });

    // Аудио-маршрут
    const [route, setRoute] = useState('auto');     // 'auto' | 'earpiece' | 'speaker'
    const [sinkId, setSinkId] = useState('default'); // десктопный output
    const [outputs, setOutputs] = useState([]);
    const deviceChangeHandlerRef = useRef(null);

    const remoteAudioRef = useRef(null);
    const remoteVideoRef = useRef(null);    // видимое видео (для видеозвонков)
    const iosRouteVideoRef = useRef(null);  // скрытое видео для iOS-фолбэка
    const localVideoRef = useRef(null);

    const peerName = useMemo(() => {
        const explicit = getPeerName ? getPeerName(st.peerId) : '';
        if (explicit) return explicit;

        const targetChat = (chats || []).find((chat) => (
            String(chat?._id || '') === String(st.chatId || '')
        ));
        const participants = Array.isArray(targetChat?.participants) ? targetChat.participants : [];
        const peer = participants.find((item) => (
            String(item?._id || item?.id || item || '') !== String(meId || '')
        ));
        const name = [peer?.firstName, peer?.lastName].filter(Boolean).join(' ').trim();

        return name || peer?.email || targetChat?.title || '';
    }, [getPeerName, st.peerId, st.chatId, chats, meId]);

    // Таймер разговора
    useEffect(() => {
        let id;
        if (st.status === 'in-call' && st.startedAt) {
            const tick = () => setElapsed(Date.now() - st.startedAt);
            tick();
            id = setInterval(tick, 1000);
        } else {
            setElapsed(0);
        }
        return () => id && clearInterval(id);
    }, [st.status, st.startedAt]);

    // Автосброс свёрнутости
    useEffect(() => { if (st.status === 'idle') setMin(false); }, [st.status]);

    // Подписки на стримы/качество
    useEffect(() => {
        const offRemote = callService.on('remote-stream-updated', (stream) => {
            if (remoteAudioRef.current) remoteAudioRef.current.srcObject = stream;
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
            if (iosRouteVideoRef.current) iosRouteVideoRef.current.srcObject = stream;

            // Мобайл: как только пришёл удалённый звук — применяем фолбэк
            if (isMobile() && st.mode !== 'video') {
                try {
                    routeFallbackIOS({
                        remoteAudioEl: remoteAudioRef.current,
                        remoteVideoEl: remoteVideoRef.current || iosRouteVideoRef.current,
                        stream,
                        to: (route === 'auto' ? 'earpiece' : route),
                    });
                } catch { }
            }
        });
        const offLocal = callService.on('local-stream-updated', (stream) => {
            if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        });
        const offQuality = callService.on('quality', (q) => setQuality(q));

        // Первичная привязка
        const r = callService.getRemoteStream?.();
        if (r && (r.getAudioTracks().length || r.getVideoTracks().length)) {
            if (remoteAudioRef.current) remoteAudioRef.current.srcObject = r;
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = r;
            if (iosRouteVideoRef.current) iosRouteVideoRef.current.srcObject = r;
        }
        const l = callService.getLocalStream?.();
        if (l && localVideoRef.current) localVideoRef.current.srcObject = l;

        return () => { offRemote(); offLocal(); offQuality(); };
    }, [route, st.mode]);

    // Десктопные выходы (selectAudioOutput / setSinkId)
    useEffect(() => {
        const refreshOutputs = async () => {
            try {
                if (canSetSink() && navigator.mediaDevices?.enumerateDevices) {
                    const outs = await listAudioOutputs();
                    setOutputs(outs);
                } else {
                    setOutputs([]);
                }
            } catch {
                setOutputs([]);
            }
        };
        refreshOutputs();

        if (navigator.mediaDevices?.addEventListener) {
            deviceChangeHandlerRef.current = refreshOutputs;
            navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandlerRef.current);
            return () => navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandlerRef.current);
        }
    }, [st.status]);

    // Применяем маршрут
    useEffect(() => {
        const apply = async () => {
            const a = remoteAudioRef.current;
            const v = remoteVideoRef.current;
            const rv = iosRouteVideoRef.current;
            const stream = callService.getRemoteStream?.();
            if (!a || !stream) return;

            if (st.mode === 'video') {
                if (canSetSink() && typeof sinkId === 'string') await routeToDevice(a, sinkId);
                return;
            }
            const want = (route === 'auto') ? 'earpiece' : route;

            if (canSetSink()) {
                await routeToDevice(a, sinkId);
            } else if (isIOS()) {
                routeFallbackIOS({ remoteAudioEl: a, remoteVideoEl: v || rv, stream, to: want });
            } else {
                // Android mobile — системное поведение
            }
        };
        apply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [st.status, st.mode, route, sinkId]);

    // Дефолт на мобилках — «телефон»
    useEffect(() => {
        if (st.status === 'in-call' && isMobile() && st.mode !== 'video') setRoute('earpiece');
    }, [st.status, st.mode]);

    const onAccept = async () => { await unlockAudio(); dispatch(acceptCall()); };
    const onReject = () => dispatch(rejectCall());
    const onHangup = () => dispatch(hangup());

    const onMute = () => dispatch(toggleMic());
    const onCam = () => dispatch(toggleCam());
    const onMinimize = () => setMin(true);
    const onRestore = () => setMin(false);

    const flipCamera = async () => {
        const next = facing === 'user' ? 'environment' : 'user';
        try { await callService.switchCamera(next); setFacing(next); }
        catch (e) { alert('Не удалось переключить камеру: ' + (e?.message || '')); }
    };

    // Тумблер спикер/телефон
    const toggleSpeaker = async () => {
        const next = route === 'speaker' ? 'earpiece' : 'speaker';
        setRoute(next);

        // На десктопе подберём sink по ярлыку
        if (canSetSink() && outputs.length) {
            const labelMatch = next === 'speaker'
                ? /speaker|динамик|altavoz|haut-parleur|гарнитура|bluetooth/i
                : /ear|receiver|телефон|phone|auricular/i;
            const cand = outputs.find(o => labelMatch.test(o.label || ''));
            if (cand) setSinkId(cand.deviceId);
            else if (next === 'speaker') setSinkId('default'); // дефолт ОС часто = колонки
        }
    };

    // Десктопный системный пикер (если есть)
    const chooseOutput = async () => {
        if (!canPickOutput()) return;
        try {
            const id = await pickOutputDeviceId(); // может вернуть 'default' | 'communications' | реальный deviceId
            if (typeof id === 'string') setSinkId(id);
        } catch (e) {
            console.warn('[CallOverlay] selectAudioOutput aborted', e?.message || e);
        }
    };

    // Селект вывода — десктоп (setSinkId): нормализуем список и ключи
    const sinkOptions = useMemo(() => {
        const list = outputs || [];
        const base = [{ deviceId: 'default', label: 'Системный по умолчанию' }];
        if (list.some(o => o.deviceId === 'communications')) {
            base.push({ deviceId: 'communications', label: 'Коммуникационное устройство' });
        }
        const rest = list
            .filter(o => o.deviceId !== 'default' && o.deviceId !== 'communications')
            .map((o, i) => ({ deviceId: o.deviceId, label: o.label || `Устройство #${i + 1}` }));
        return [...base, ...rest];
    }, [outputs]);

    const handleSinkSelect = async (e) => {
        const id = e.target.value; // 'default' | 'communications' | реальный deviceId
        setSinkId(id);
        const a = remoteAudioRef.current;
        if (a) await routeToDevice(a, id);
    };

    const statusText =
        st.status === 'in-call' ? fmt(elapsed)
            : st.reconnecting ? 'Восстановление связи…'
                : st.status === 'connecting' ? 'Соединение…'
                : st.status === 'dialing' ? 'Вызов…'
                    : st.incoming ? 'Входящий…' : '';

    if (st.status === 'idle') return null;

    const renderQuality = () => (
        <div
            className="signal-bars"
            title={`rtt=${quality.rtt}ms • jitter=${quality.jitter}ms • loss=${quality.lossPct}% • in=${quality.bitrateIn}kbps • out=${quality.bitrateOut}kbps`}
        >
            {[1, 2, 3, 4, 5].map(i => (
                <span
                    key={`q-${i}`}
                    className={`signal-bars__bar ${i <= (quality.level || 0) ? 'signal-bars__bar--on' : ''}`}
                    style={{ height: i * 5 }}
                />
            ))}
        </div>
    );

    const headerRight = (
        <div className="call-overlay__header-right">
            {st.status === 'in-call' && renderQuality()}
            {min
                ? <button onClick={onRestore} className="btn btn--ghost">Развернуть</button>
                : <button onClick={onMinimize} className="btn btn--ghost">Свернуть</button>}
            <button onClick={onHangup} className="btn btn--danger">Завершить</button>
        </div>
    );

    const showOutputSelect = canSetSink();

    return (
        <>
            {/* НЕ скрываем display:none — некоторые мобильные стэки не роутят такое аудио */}
            <audio
                ref={remoteAudioRef}
                autoPlay
                playsInline
                className="call-overlay__hidden-audio"
            />
            {/* Скрытое видео для iOS-фолбэка маршрута даже при аудиозвонках */}
            <video
                ref={iosRouteVideoRef}
                autoPlay
                playsInline
                x-webkit-airplay="allow"
                className="call-overlay__hidden-video"
            />

            {min ? (
                <div className="call-overlay__bar">
                    <div className="call-overlay__bar-title">{peerName || 'Звонок'}</div>
                    <div className="call-overlay__bar-timer">{statusText}</div>
                    {headerRight}
                </div>
            ) : (
                <div className="call-overlay">
                    <div className="call-overlay__top">
                        <div className="call-overlay__title">{peerName || 'Звонок'}</div>
                        <div className="call-overlay__status">{statusText}</div>
                        {headerRight}
                    </div>

                    <div className={`call-overlay__media ${st.mode === 'video' ? 'call-overlay__media--video' : ''}`}>
                        <video
                            ref={remoteVideoRef}
                            autoPlay
                            playsInline
                            x-webkit-airplay="allow"
                            className="call-overlay__remote-video"
                        />
                        {st.mode !== 'video' && (
                            <div className="call-overlay__avatar">
                                {(peerName || 'П')[0].toUpperCase()}
                            </div>
                        )}
                        {st.mode === 'video' && (
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="call-overlay__local-video"
                            />
                        )}
                    </div>

                    <div className="call-overlay__controls">
                        {st.incoming && st.status === 'ringing' ? (
                            <>
                                <button onClick={onReject} className="btn btn--danger btn--big">Отклонить</button>
                                <button onClick={onAccept} className="btn btn--success btn--big">Принять</button>
                            </>
                        ) : (
                            <>
                                <button onClick={onMute} className={`btn ${st.mic ? 'btn--ghost' : 'btn--warn'}`}>
                                    {st.mic ? 'Микрофон вкл' : 'Микрофон выкл'}
                                </button>

                                {st.mode === 'video' && (
                                    <>
                                        <button onClick={onCam} className={`btn ${st.cam ? 'btn--ghost' : 'btn--warn'}`}>
                                            {st.cam ? 'Камера вкл' : 'Камера выкл'}
                                        </button>
                                        <button onClick={flipCamera} className="btn btn--ghost">Поменять камеру</button>
                                    </>
                                )}
                                {/* Десктоп: системный пикер и явный селект */}
                                {canPickOutput() && (
                                    <button onClick={chooseOutput} className="btn btn--ghost">🧭 Выбрать выход</button>
                                )}
                                {showOutputSelect && (
                                    <div className="call-overlay__select-wrap">
                                        <label className="call-overlay__select-label">Вывод:&nbsp;</label>
                                        <select
                                            value={sinkId}
                                            onChange={handleSinkSelect}
                                            className="call-overlay__select"
                                            title="Куда выводить звук"
                                        >
                                            {sinkOptions.map(o => (
                                                <option key={`sink-${o.deviceId}`} value={o.deviceId}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
